import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiError, isAdmin } from "@/lib/api";

export default function Login() {
  const { user, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const home = (u) => (u.must_change_password ? "/change-password" : isAdmin(u) ? "/admin" : "/portal");
  if (user) return <Navigate to={home(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(home(u), { replace: true });
    } catch (err) {
      setError(apiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]" data-testid="login-page">
      <section className="grain relative flex flex-col justify-between overflow-hidden bg-primary px-8 py-10 text-primary-foreground sm:px-14 lg:py-14" data-testid="login-brand-panel">
        <div className="relative text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-foreground/70">Private Lending · Est. Root Equity Partners</div>
        <div className="relative mx-auto my-10 w-full max-w-[400px] fade-up rounded-lg bg-[#FBFAF5] p-8 shadow-[0_24px_60px_-28px_rgba(0,0,0,0.55)] ring-1 ring-black/5 sm:p-10" data-testid="login-logo-plate">
          <Logo light className="w-full" testid="login-logo" />
        </div>
        <div className="relative">
          <p className="font-display text-2xl italic sm:text-3xl" data-testid="login-tagline">Come Grow With Us</p>
          <div className="gold-rule mt-4 w-32" />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-primary-foreground/75">
            Transparent schedules, precise balances, and a clear view of every payment — rooted in trust.
          </p>
        </div>
      </section>

      <section className="flex items-center justify-center px-6 py-14 sm:px-12">
        <form onSubmit={submit} className="fade-up w-full max-w-sm space-y-7" data-testid="login-form" style={{ animationDelay: "120ms" }}>
          <div>
            <div className="eyebrow">Client & Lender Portal</div>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight">Sign in</h1>
            <p className="mt-2 text-sm text-muted-foreground">Use the credentials provided by your lender.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className="h-11 bg-card" data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" required value={password}
              onChange={(e) => setPassword(e.target.value)} className="h-11 bg-card" data-testid="login-password-input" />
          </div>
          {error && (
            <div role="alert" className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive" data-testid="login-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          <Button type="submit" disabled={busy} className="h-11 w-full text-sm tracking-wide" data-testid="login-submit-button">
            {busy ? <Loader2 className="animate-spin" /> : <>Sign in <ArrowRight /></>}
          </Button>
          <p className="text-center text-sm text-muted-foreground">New to Root Equity Partners? <Link to="/apply" className="font-semibold text-primary hover:underline" data-testid="login-apply-link">Apply for a loan</Link></p>
        </form>
      </section>
    </div>
  );
}
